import FuturesSignalRoutesCard from '@/features/strategyBacktests/FuturesSignalRoutesCard';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { trpc, trpcUnbatched } from '@/api/trpc.ts';
import { buildTradingStrategyValidationErrors, resolveStrategyEditorSectionForValidationErrors } from './components/helpers';
import TradeLogicCapabilityErrorCard from './components/TradeLogicCapabilityErrorCard';
import StrategyIdentityHeader from './components/StrategyIdentityHeader';
import StrategySectionNav from './components/StrategySectionNav';
import StrategySaveBar from './components/StrategySaveBar';
import StrategyOverviewPane, { type StrategyOverviewLiveReadiness } from './components/StrategyOverviewPane';
import StrategyCreatePane from './components/StrategyCreatePane';
import StrategyBacktestsPane from './components/StrategyBacktestsPane';
import BasicInformationCard from './components/BasicInformationCard';
import TradingConfigurationCard from './components/TradingConfigurationCard';
import RegimeModelPinsCard from './components/RegimeModelPinsCard';
import ScreenerGatingCard, { buildOptimizerConfirmationNotice } from './components/ScreenerGatingCard';
import SessionTimingPolicyCard from './components/SessionTimingPolicyCard';
import EntryScorecardCard from './components/EntryScorecardCard';
import GlobalExitCandidatesCard, { type GlobalExitCandidatesCardHandle } from './components/GlobalExitCandidatesCard';
import StrategyLegsSection from './components/StrategyLegsSection';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { parseTRPCError } from '@/api/parseTrpcError.ts';
import { getTradeLogicCapabilityError, type TradeLogicCapabilityErrorDetails } from '@/api/getTradeLogicCapabilityError.ts';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants.ts';
import { Button } from '@/components/UI';
import ConfirmationDialog from '@/components/UI/ConfirmationDialog';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useScreenerRuleOptions } from '@/hooks/useScreenerRuleOptions';
import { useTradingRequisites } from '@/hooks/useTradingRequisites';
import ResourceAccessNotice from '@/components/resourceAccess/ResourceAccessNotice';
import ShareResourceModal from '@/components/resourceAccess/ShareResourceModal';
import TradeLogicV2ExecutionJournalViewer from './TradeLogicV2ExecutionJournalViewer.tsx';
import TradeLogicAttachmentCard from '@/features/tradeLogicAuthoring/components/TradeLogicAttachmentCard';
import type { TradeLogicAttachmentChange } from '@/features/tradeLogicAuthoring/components/attachmentEditorState';
import { invalidatePublishedTradeLogicQueries } from '@/features/tradeLogicAuthoring/api';
import {
    consumeTradeLogicAttachmentSearchParams,
    type TradeLogicAttachmentReturnState
} from '@/features/tradeLogicAuthoring/tradeLogicNavigation';
import {
    getTradingStrategyEditorAuxiliaryQueryState,
    getTradingStrategyEditorLoadingState,
    parseTradingStrategyRouteId
} from './tradingStrategyEditorLoadingState';
import {
    buildStrategyEditorFormChanges,
    buildStrategyEditorScreenerChanges,
    buildStrategyEditorSectionChangeCounts,
    countVisibleTradingStrategyLegs,
    type StrategyEditorChange
} from './strategyEditorChanges';
import { buildRegimePinChanges, hasRegimePinChanges, resolveStrategySaveGate, type StrategySaveGate } from './regimePinsPresentation';
import {
    getVisibleStrategyEditorSections,
    resolveActiveStrategyEditorSection,
    type StrategyEditorSectionId
} from './strategyEditorSections';
import {
    buildCreateTradingStrategyInput,
    applyEntryTradeLogicAttachmentChange,
    applyExitTradeLogicAttachmentChange,
    buildTradingStrategyFormDataFromStrategy,
    buildTradingStrategyLegMutationInputs,
    buildUpdateTradingStrategyInput,
    executeTradingStrategyLegMutations,
    getTradingStrategyLegSummary,
    hasRequiredTradeLogicV2Attachments,
    hasVisibleTradingStrategyOptionLeg,
    type TradingStrategyFormData
} from './tradingStrategyEditorSubmitPayloads';
import { showToast } from '@/components/Layout';
import { LIVE_READINESS_RECHECK_INTERVAL_MS, describeLiveReadinessCallout } from '@/utils/liveReadinessCallout';

const TradingStrategyEditor: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();
    const copyFromId = searchParams.get('copyFrom');
    const requestedSectionId = searchParams.get('section');
    const isEditMode = id !== undefined && id !== 'new';
    const isCopyMode = !isEditMode && copyFromId !== null;
    const strategyId = parseTradingStrategyRouteId(id);
    const copyFromStrategyId = parseTradingStrategyRouteId(copyFromId ?? undefined);
    const isRouteStrategyIdValid = !isEditMode || strategyId !== null;
    const strategyNameInputRef = useRef<HTMLInputElement | null>(null);
    const globalExitCandidatesCardRef = useRef<GlobalExitCandidatesCardHandle | null>(null);
    const pendingRouteTradeLogicAttachmentRef = useRef<TradeLogicAttachmentReturnState | null>(null);

    const [error, setError] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    const [tradeLogicV2EntryCapabilityError, setTradeLogicV2EntryCapabilityError] = useState<TradeLogicCapabilityErrorDetails | null>(null);

    const [tradeLogicV2ExitCapabilityError, setTradeLogicV2ExitCapabilityError] = useState<TradeLogicCapabilityErrorDetails | null>(null);
    const [formHydrationKey, setFormHydrationKey] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showAttachmentSaveConfirm, setShowAttachmentSaveConfirm] = useState(false);
    const [showRegimePinSaveConfirm, setShowRegimePinSaveConfirm] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [isSubmitWorkflowPending, setIsSubmitWorkflowPending] = useState(false);
    const [candidateChangeLabels, setCandidateChangeLabels] = useState<string[]>([]);
    /** Save failures stay in the save bar. Replacing the page would strand edits staged across sections. */
    const [saveError, setSaveError] = useState<string | null>(null);

    // Strategy-level screener attachment
    const [strategyScreenerRuleId, setStrategyScreenerRuleId] = useState<number | null>(null);
    const [isScreenerAttachmentInitialized, setIsScreenerAttachmentInitialized] = useState<boolean>(false);

    // Initial form state
    const [formData, setFormData] = useState<TradingStrategyFormData>({
        name: '',
        description: '',
        active: true,
        autoBacktest: false,
        autoBacktestOptionBuyingStrategiesOverride: undefined,
        allowedUnderlyingEntityTypes: ['Asset'],
        duration: '1m',
        afterHoursRemoved: true,
        entityListId: null,
        entryLogicDefinitionId: null,
        entryLogicVersion: null,
        tradeLogicV2EntryVersionId: null,
        exitLogicDefinitionId: null,
        exitLogicVersion: null,
        tradeLogicV2ExitVersionId: null,
        sessionTimingPolicy: null,
        regimeModelVersionOverrides: null,
        legs: [
            {
                name: 'Main Leg',
                description: '',
                optionType: null,
                derivativeType: null,
                openTransactionType: 'buy',
                entryTransactionSizeType: 'percentageRiskCapital',
                entryTransactionSizeValue: 100,
                isNew: true,
                isModified: false,
                isDeleted: false
            }
        ]
    });

    /** Snapshot of the persisted strategy, taken at hydration; every unsaved-change label is a diff against it. */
    const [baselineFormData, setBaselineFormData] = useState<TradingStrategyFormData | null>(null);

    const strategyHasOptionLeg = useMemo(() => hasVisibleTradingStrategyOptionLeg(formData.legs), [formData.legs]);

    // Fetch existing strategy data first in edit mode. Auxiliary editor queries are gated behind this query so slow
    // backend surfaces cannot keep the critical route shell stuck in a shared browser connection queue.
    const strategyQuery = useAppQuery(
        {
            ...trpcUnbatched.strategy.fetchStrategies.queryOptions({
                ids: strategyId ? [strategyId] : [],
                requireActive: false
            }),
            enabled: !!isEditMode && isRouteStrategyIdValid,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load trading strategy',
            setError: (msg) => setError(msg)
        }
    );

    // Fetch source strategy if in copy mode. Auxiliary editor queries wait for this in copy mode so the source
    // strategy request is not forced to compete with lower-priority registry/requisite requests during page load.
    const copySourceQuery = useAppQuery({
        ...trpcUnbatched.strategy.fetchStrategies.queryOptions({
            ids: copyFromStrategyId ? [copyFromStrategyId] : [],
            requireActive: false
        }),
        enabled: isCopyMode && copyFromStrategyId !== null,
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const persistedStrategy = strategyQuery.data?.strategies?.[0] ?? null;
    const copySourceStrategy = copySourceQuery.data?.strategies?.[0] ?? null;

    const expectedFormHydrationKey =
        isEditMode && persistedStrategy !== null
            ? `edit:${strategyId ?? 'unknown'}`
            : isCopyMode && copySourceStrategy !== null
              ? `copy:${copyFromStrategyId ?? 'unknown'}`
              : null;

    const isFormHydrating = expectedFormHydrationKey !== null && formHydrationKey !== expectedFormHydrationKey;

    const { canLoadPersistedStrategyAuxiliaryData, canLoadStrategyEditorAuxiliaryData } = getTradingStrategyEditorAuxiliaryQueryState({
        hasCopySourceStrategy: copySourceStrategy !== null,
        hasPersistedStrategy: persistedStrategy !== null,
        isCopyMode,
        isEditMode,
        isStrategyFetchSuccess: strategyQuery.isSuccess,
        strategyId
    });

    const hasPersistedTradeLogicV2Entry =
        persistedStrategy?.tradeLogicV2EntryVersionId !== null && persistedStrategy?.tradeLogicV2EntryVersionId !== undefined;

    const hasPersistedTradeLogicV2Exit =
        persistedStrategy?.tradeLogicV2ExitVersionId !== null && persistedStrategy?.tradeLogicV2ExitVersionId !== undefined;

    const accessSummaryQuery = useQuery({
        ...trpc.resourceAccess.getAccessSummary.queryOptions({
            resourcePk: strategyId ?? 0,
            resourceType: 'tradingStrategy'
        }),
        enabled: canLoadPersistedStrategyAuxiliaryData
    });

    const isAccessSummaryLoading =
        canLoadPersistedStrategyAuxiliaryData && accessSummaryQuery.data === undefined && accessSummaryQuery.isLoading;

    const canEditStrategy = !isEditMode || accessSummaryQuery.data?.canEdit === true;
    const isOwnedStrategy = !isEditMode || accessSummaryQuery.data?.ownedByUser === true;

    // Resolved before the section-scoped queries below so each one can gate itself on the active
    // section: a pane that is not mounted must not fetch, and must not raise its error toasts.
    const visibleSections = useMemo(
        () =>
            getVisibleStrategyEditorSections({
                hasPersistedStrategy: persistedStrategy !== null,
                isEditMode: Boolean(isEditMode),
                isOwnedStrategy
            }),
        [isEditMode, isOwnedStrategy, persistedStrategy]
    );

    const activeSectionId = resolveActiveStrategyEditorSection(requestedSectionId, visibleSections);

    const selectSection = (sectionId: StrategyEditorSectionId) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('section', sectionId);

        void navigate({ pathname: location.pathname, search: `?${nextSearchParams.toString()}` }, { replace: true });
    };

    // Shared requisites cache: child selects consume it via the same query key, and the regime pin
    // card reads the regime model catalog from the captured result.
    const tradingRequisitesResult = useTradingRequisites({
        enabled: canLoadStrategyEditorAuxiliaryData,
        errorConfig: {
            fallbackErrorMessage: 'Failed to load form options',
            showErrorToast: true
        }
    });

    // Warms the entry definitions cache and surfaces fetch errors; the data is consumed via the shared query key
    useAppQuery(
        {
            ...trpcUnbatched.strategy.fetchTradeLogicDefinitions.queryOptions({
                kind: 'entry'
            }),
            enabled: canLoadStrategyEditorAuxiliaryData,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load Trade Logic V2 entry definitions',
            setError: (msg) => setError(msg)
        }
    );

    // Warms the versions cache for the selected entry definition and surfaces fetch errors; the data is consumed via the shared query key
    useAppQuery(
        {
            ...trpcUnbatched.strategy.fetchTradeLogicDefinitionVersions.queryOptions({
                definitionId: formData.entryLogicDefinitionId ?? 0
            }),
            enabled: canLoadStrategyEditorAuxiliaryData && formData.entryLogicDefinitionId !== null,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load Trade Logic V2 entry definition versions',
            setError: (msg) => setError(msg)
        }
    );

    // Warms the exit definitions cache and surfaces fetch errors; the data is consumed via the shared query key
    useAppQuery(
        {
            ...trpcUnbatched.strategy.fetchTradeLogicDefinitions.queryOptions({
                kind: 'exit'
            }),
            enabled: canLoadStrategyEditorAuxiliaryData,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load Trade Logic V2 exit definitions',
            setError: (msg) => setError(msg)
        }
    );

    // Warms the versions cache for the selected exit definition and surfaces fetch errors; the data is consumed via the shared query key
    useAppQuery(
        {
            ...trpcUnbatched.strategy.fetchTradeLogicDefinitionVersions.queryOptions({
                definitionId: formData.exitLogicDefinitionId ?? 0
            }),
            enabled: canLoadStrategyEditorAuxiliaryData && formData.exitLogicDefinitionId !== null,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load Trade Logic V2 exit definition versions',
            setError: (msg) => setError(msg)
        }
    );

    const selectedTradeLogicV2EntryVersionQuery = useAppQuery(
        {
            ...trpcUnbatched.strategy.fetchTradeLogicDefinitionVersionById.queryOptions({
                versionId: formData.tradeLogicV2EntryVersionId ?? 0
            }),
            enabled: canLoadStrategyEditorAuxiliaryData && formData.tradeLogicV2EntryVersionId !== null,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load selected Trade Logic V2 entry version',
            setError: (msg) => setError(msg)
        }
    );

    const selectedTradeLogicV2ExitVersionQuery = useAppQuery(
        {
            ...trpcUnbatched.strategy.fetchTradeLogicDefinitionVersionById.queryOptions({
                versionId: formData.tradeLogicV2ExitVersionId ?? 0
            }),
            enabled: canLoadStrategyEditorAuxiliaryData && formData.tradeLogicV2ExitVersionId !== null,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load selected Trade Logic V2 exit version',
            setError: (msg) => setError(msg)
        }
    );

    const persistedTradeLogicV2EntryVersionQuery = useAppQuery(
        {
            ...trpcUnbatched.strategy.fetchTradeLogicDefinitionVersionById.queryOptions({
                versionId: persistedStrategy?.tradeLogicV2EntryVersionId ?? 0
            }),
            enabled: canLoadPersistedStrategyAuxiliaryData && (persistedStrategy?.tradeLogicV2EntryVersionId ?? null) !== null,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load live Trade Logic V2 entry attachment',
            setError: (msg) => setError(msg)
        }
    );

    const persistedTradeLogicV2ExitVersionQuery = useAppQuery(
        {
            ...trpcUnbatched.strategy.fetchTradeLogicDefinitionVersionById.queryOptions({
                versionId: persistedStrategy?.tradeLogicV2ExitVersionId ?? 0
            }),
            enabled: canLoadPersistedStrategyAuxiliaryData && (persistedStrategy?.tradeLogicV2ExitVersionId ?? null) !== null,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load live Trade Logic V2 exit attachment',
            setError: (msg) => setError(msg)
        }
    );

    const liveEntryAttachmentDefinition = persistedTradeLogicV2EntryVersionQuery.data?.definition ?? null;
    const liveEntryAttachmentDefinitionId = liveEntryAttachmentDefinition?.definitionId ?? null;
    const liveEntryAttachmentVersion = persistedTradeLogicV2EntryVersionQuery.data?.version?.version ?? null;
    const liveExitAttachmentDefinition = persistedTradeLogicV2ExitVersionQuery.data?.definition ?? null;
    const liveExitAttachmentDefinitionId = liveExitAttachmentDefinition?.definitionId ?? null;
    const liveExitAttachmentVersion = persistedTradeLogicV2ExitVersionQuery.data?.version?.version ?? null;

    // A runtime deploy can stop accepting an attached version after activation, so the live status
    // comes from the runtime's own check rather than the stored active flag. It drives the overview
    // and its nav badge, so it is not scoped to the active section.
    const canCheckLiveReadiness = canLoadPersistedStrategyAuxiliaryData && canEditStrategy && hasPersistedTradeLogicV2Entry;
    const liveReadinessQuery = useQuery({
        ...trpcUnbatched.strategy.fetchStrategyLiveReadiness.queryOptions({ strategyId: strategyId ?? 0 }),
        enabled: canCheckLiveReadiness,
        refetchInterval: (query) => {
            const gap = query.state.data?.gap;
            return gap && describeLiveReadinessCallout(gap).shouldRecheck ? LIVE_READINESS_RECHECK_INTERVAL_MS : false;
        }
    });
    const overviewLiveReadiness: StrategyOverviewLiveReadiness = !canCheckLiveReadiness
        ? { state: 'notChecked' }
        : liveReadinessQuery.data !== undefined
          ? { state: 'checked', gap: liveReadinessQuery.data.gap }
          : liveReadinessQuery.isError
            ? { state: 'failed', message: liveReadinessQuery.error.message }
            : { state: 'checking' };

    const canAttachScreenerToPersistedStrategy = !!strategyId;

    const screenerRuleOptionsResult = useScreenerRuleOptions(strategyScreenerRuleId, {
        enabled: canLoadPersistedStrategyAuxiliaryData,
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const tradeLogicV2EntryJournalsQuery = useAppQuery(
        {
            ...trpc.strategy.fetchTradeLogicExecutionJournals.queryOptions({
                definitionId: liveEntryAttachmentDefinitionId ?? 0,
                ...(liveEntryAttachmentVersion != null ? { version: liveEntryAttachmentVersion } : {}),
                ...(strategyId != null ? { strategyId } : {}),
                limit: 8
            }),
            enabled:
                activeSectionId === 'activity' &&
                canLoadPersistedStrategyAuxiliaryData &&
                hasPersistedTradeLogicV2Entry &&
                liveEntryAttachmentDefinitionId !== null,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load Trade Logic V2 entry execution journals',
            showErrorToast: true
        }
    );

    const tradeLogicV2ExitJournalsQuery = useAppQuery(
        {
            ...trpc.strategy.fetchTradeLogicExecutionJournals.queryOptions({
                definitionId: liveExitAttachmentDefinitionId ?? 0,
                ...(liveExitAttachmentVersion != null ? { version: liveExitAttachmentVersion } : {}),
                ...(strategyId != null ? { strategyId } : {}),
                limit: 8
            }),
            enabled:
                activeSectionId === 'activity' &&
                canLoadPersistedStrategyAuxiliaryData &&
                hasPersistedTradeLogicV2Exit &&
                liveExitAttachmentDefinitionId !== null,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load Trade Logic V2 exit execution journals',
            showErrorToast: true
        }
    );

    // Backtest coverage summary for the identity header and overview. Shares a query key with the
    // Backtests section's operations surface, so React Query serves both observers from one fetch.
    const backtestCoverageSummaryQuery = useAppQuery(
        {
            ...trpc.backtest.getStrategyCoverage.queryOptions({
                strategyId: strategyId ?? 0,
                entityType: null
            }),
            enabled: canLoadPersistedStrategyAuxiliaryData && isOwnedStrategy && strategyId !== null
        },
        {
            fallbackErrorMessage: 'Failed to fetch backtest coverage for this strategy',
            showErrorToast: false
        }
    );

    // Shares a query key with GlobalExitCandidatesCard, so the header chip and overview add no extra fetch.
    const strategyCandidateAttachmentsQuery = useQuery({
        ...trpc.tradeLogicExitCandidates.getStrategyCandidateAttachments.queryOptions({
            strategyId: strategyId ?? 0
        }),
        enabled: canLoadPersistedStrategyAuxiliaryData && strategyId !== null
    });

    const attachedCandidates = strategyCandidateAttachmentsQuery.data?.candidates ?? null;
    const attachedCandidateCount = attachedCandidates?.length ?? null;
    const defaultCandidateId = attachedCandidates?.find((candidate) => candidate.isDefault)?.candidateId ?? null;

    const backtestCoverage = backtestCoverageSummaryQuery.data ?? null;

    const backtestCoveragePercent =
        backtestCoverage && backtestCoverage.totalEntitiesInUniverse > 0
            ? Math.round((backtestCoverage.entitiesWithBacktestsCount / backtestCoverage.totalEntitiesInUniverse) * 100)
            : null;

    const persistedScreenerRuleId = persistedStrategy?.activeScreenerRuleId ?? null;

    useEffect(() => {
        setIsScreenerAttachmentInitialized(false);
        setStrategyScreenerRuleId(null);
        setFormHydrationKey(null);
        setBaselineFormData(null);
        setCandidateChangeLabels([]);
        pendingRouteTradeLogicAttachmentRef.current = null;
    }, [strategyId]);

    useEffect(() => {
        if (!strategyId || !isEditMode) return;
        if (isScreenerAttachmentInitialized) return;
        if (!strategyQuery.isFetched) return;

        setStrategyScreenerRuleId(persistedScreenerRuleId);
        setIsScreenerAttachmentInitialized(true);
    }, [strategyId, isEditMode, isScreenerAttachmentInitialized, strategyQuery.isFetched, persistedScreenerRuleId]);

    // Update form data when strategy is loaded (edit or copy mode)
    useEffect(() => {
        const routeAttachment = pendingRouteTradeLogicAttachmentRef.current;

        if (strategyQuery.data?.strategies?.[0] && isEditMode) {
            const hydrationKey = `edit:${strategyId ?? 'unknown'}`;
            if (formHydrationKey === hydrationKey) return;

            const hydratedFormData = buildTradingStrategyFormDataFromStrategy(strategyQuery.data.strategies[0], 'edit', routeAttachment);

            setFormData(hydratedFormData);
            // The baseline ignores any attachment handed back by the rule editor, so returning from a
            // revision correctly reads as an unsaved change rather than as already-persisted state.
            setBaselineFormData(buildTradingStrategyFormDataFromStrategy(strategyQuery.data.strategies[0], 'edit'));
            setFormHydrationKey(hydrationKey);
            // Consumed: re-hydrating after a save must not re-apply an attachment from an earlier visit.
            pendingRouteTradeLogicAttachmentRef.current = null;
        } else if (copySourceQuery.data?.strategies?.[0] && isCopyMode) {
            const hydrationKey = `copy:${copyFromStrategyId ?? 'unknown'}`;
            if (formHydrationKey === hydrationKey) return;

            setFormData(buildTradingStrategyFormDataFromStrategy(copySourceQuery.data.strategies[0], 'copy', routeAttachment));
            setFormHydrationKey(hydrationKey);
            pendingRouteTradeLogicAttachmentRef.current = null;
        }
    }, [strategyQuery.data, copySourceQuery.data, formHydrationKey, isEditMode, isCopyMode, strategyId, copyFromStrategyId]);

    useEffect(() => {
        const version = selectedTradeLogicV2EntryVersionQuery.data?.version ?? null;
        const definition = selectedTradeLogicV2EntryVersionQuery.data?.definition ?? null;

        if (!version || !definition) return;

        setFormData((previousFormData) => {
            if (previousFormData.tradeLogicV2EntryVersionId !== version.versionId) return previousFormData;

            if (
                previousFormData.entryLogicDefinitionId === definition.definitionId &&
                previousFormData.entryLogicVersion === version.version
            ) {
                return previousFormData;
            }

            return {
                ...previousFormData,
                entryLogicDefinitionId: definition.definitionId,
                entryLogicVersion: version.version
            };
        });
        // formData ids are dependencies too: discarding or re-hydrating resets them to null without
        // changing the query result, and the backfill has to reconcile them again. The updater's
        // early-returns keep this idempotent, so it settles after one extra pass.
    }, [
        formData.entryLogicDefinitionId,
        formData.entryLogicVersion,
        formData.tradeLogicV2EntryVersionId,
        selectedTradeLogicV2EntryVersionQuery.data?.definition,
        selectedTradeLogicV2EntryVersionQuery.data?.version
    ]);

    useEffect(() => {
        const version = selectedTradeLogicV2ExitVersionQuery.data?.version ?? null;
        const definition = selectedTradeLogicV2ExitVersionQuery.data?.definition ?? null;

        if (!version || !definition) return;

        setFormData((previousFormData) => {
            if (previousFormData.tradeLogicV2ExitVersionId !== version.versionId) return previousFormData;

            if (
                previousFormData.exitLogicDefinitionId === definition.definitionId &&
                previousFormData.exitLogicVersion === version.version
            ) {
                return previousFormData;
            }

            return {
                ...previousFormData,
                exitLogicDefinitionId: definition.definitionId,
                exitLogicVersion: version.version
            };
        });
    }, [
        formData.exitLogicDefinitionId,
        formData.exitLogicVersion,
        formData.tradeLogicV2ExitVersionId,
        selectedTradeLogicV2ExitVersionQuery.data?.definition,
        selectedTradeLogicV2ExitVersionQuery.data?.version
    ]);

    // Handle screener list query errors (useScreenerRuleOptions does not expose an errorConfig)
    useEffect(() => {
        if (screenerRuleOptionsResult.listQuery.error) {
            handleTrpcQuery(screenerRuleOptionsResult.listQuery, {
                fallbackErrorMessage: 'Failed to load screeners',
                setError: (msg) => setError(msg)
            });
        }
    }, [screenerRuleOptionsResult.listQuery.error]);

    // Create mutation
    const createStrategyMutation = useTrpcMutation(
        trpc.strategy.createStrategy.mutationOptions({
            onSuccess: () => {
                // Invalidate the strategies query to ensure the new strategy appears in the list
                void queryClient.invalidateQueries({
                    queryKey: trpc.strategy.fetchStrategies.queryOptions({ requireActive: false, ids: [] }).queryKey
                });
            }
        }),
        {
            successMessage: 'Strategy created successfully',
            fallbackErrorMessage: 'Failed to create strategy'
        }
    );

    // Update mutation
    const updateStrategyMutation = useTrpcMutation(
        trpc.strategy.updateStrategy.mutationOptions({
            onSuccess: () => {
                // Invalidate the strategies query to ensure the list is fresh when navigating back
                void queryClient.invalidateQueries({
                    queryKey: trpc.strategy.fetchStrategies.queryOptions({ requireActive: false, ids: [] }).queryKey
                });
                // Activation, duration, and session timing all change what the live runtime is asked to accept.
                void queryClient.invalidateQueries(trpcUnbatched.strategy.fetchStrategyLiveReadiness.pathFilter());
            }
        }),
        {
            fallbackErrorMessage: 'Failed to update strategy'
        }
    );

    const attachScreenerToStrategyMutation = useMutation(trpc.screener.attachToStrategy.mutationOptions());

    // Delete mutation
    const deleteStrategyMutation = useTrpcMutation(
        trpc.strategy.deleteStrategy.mutationOptions({
            onSuccess: () => {
                // Invalidate the strategies query to ensure the deleted strategy is removed from the list
                void queryClient.invalidateQueries({
                    queryKey: trpc.strategy.fetchStrategies.queryOptions({ requireActive: false, ids: [] }).queryKey
                });

                setShowDeleteConfirm(false);
                void navigate('/user/tradingStrategies');
            }
        }),
        {
            successMessage: 'Strategy deleted successfully',
            fallbackErrorMessage: 'Failed to delete strategy'
        }
    );

    // Leg mutations
    const createLegMutation = useTrpcMutation(trpc.strategy.createStrategyLeg.mutationOptions(), {
        fallbackErrorMessage: 'Failed to create leg'
    });

    const updateLegMutation = useTrpcMutation(trpc.strategy.updateStrategyLeg.mutationOptions(), {
        fallbackErrorMessage: 'Failed to update leg'
    });

    const deleteLegMutation = useTrpcMutation(trpc.strategy.deleteStrategyLeg.mutationOptions(), {
        fallbackErrorMessage: 'Failed to delete leg'
    });

    // Loading state management
    const loadingState = getTradingStrategyEditorLoadingState({
        hasPersistedStrategy: persistedStrategy !== null,
        isAccessSummaryLoading,
        isCopySourceLoading: copySourceQuery.isLoading,
        isEditMode: Boolean(isEditMode),
        isFormHydrating,
        isRouteStrategyIdValid,
        isStrategyFetchSuccess: strategyQuery.isSuccess,
        isStrategyLoading: strategyQuery.isLoading
    });

    const isScreenerAttachmentLoading = screenerRuleOptionsResult.listQuery.isLoading || strategyQuery.isLoading;

    useEffect(() => {
        if (loadingState.isLoading) {
            showSpinner();
        } else {
            hideSpinner();
        }

        return () => {
            hideSpinner();
        };
    }, [loadingState.isLoading]);

    // ---------------------------------------------------------------------------------------------
    // Unsaved changes
    // ---------------------------------------------------------------------------------------------

    const formChanges = useMemo(
        () =>
            buildStrategyEditorFormChanges(baselineFormData, formData, {
                liveEntryVersion: liveEntryAttachmentVersion,
                liveExitVersion: liveExitAttachmentVersion,
                stagedEntryVersion: formData.entryLogicVersion,
                stagedExitVersion: formData.exitLogicVersion
            }),
        [baselineFormData, formData, liveEntryAttachmentVersion, liveExitAttachmentVersion]
    );

    const screenerChanges = useMemo(
        () =>
            buildStrategyEditorScreenerChanges({
                isInitialized: isScreenerAttachmentInitialized,
                persistedScreenerRuleId,
                screenerRuleId: strategyScreenerRuleId
            }),
        [isScreenerAttachmentInitialized, persistedScreenerRuleId, strategyScreenerRuleId]
    );

    const unsavedChanges: StrategyEditorChange[] = useMemo(
        () => [
            ...formChanges,
            ...screenerChanges,
            ...candidateChangeLabels.map((label) => ({ section: 'exit' as StrategyEditorSectionId, label }))
        ],
        [candidateChangeLabels, formChanges, screenerChanges]
    );

    const changeCountsBySection = useMemo(() => buildStrategyEditorSectionChangeCounts(unsavedChanges), [unsavedChanges]);

    const hasStagedEntryAttachmentChange = formChanges.some((change) => change.label.startsWith('Entry rules'));
    const hasStagedExitAttachmentChange = formChanges.some((change) => change.label.startsWith('Exit rules'));
    const hasStagedAttachmentChange = hasStagedEntryAttachmentChange || hasStagedExitAttachmentChange;

    const hasStagedRegimePinChange =
        baselineFormData !== null &&
        hasRegimePinChanges(baselineFormData.regimeModelVersionOverrides, formData.regimeModelVersionOverrides);

    const stagedRegimePinChanges = useMemo(
        () =>
            baselineFormData === null
                ? []
                : buildRegimePinChanges(baselineFormData.regimeModelVersionOverrides, formData.regimeModelVersionOverrides),
        [baselineFormData, formData.regimeModelVersionOverrides]
    );

    // ---------------------------------------------------------------------------------------------
    // Form handlers
    // ---------------------------------------------------------------------------------------------

    const handleInputChange = <Field extends keyof TradingStrategyFormData>(field: Field, value: TradingStrategyFormData[Field]) => {
        setFormData((prev) => {
            const next = { ...prev, [field]: value };
            if (field === 'allowedUnderlyingEntityTypes' && next.allowedUnderlyingEntityTypes.includes('FuturesProduct')) {
                next.active = false;
                next.autoBacktest = false;
                if (next.allowedUnderlyingEntityTypes.includes('FuturesProduct')) {
                    next.duration = '1m';
                    next.afterHoursRemoved = false;
                    next.entityListId = null;
                }
            }
            return next;
        });
        // Clear validation error for this field
        setValidationErrors((prev) => ({ ...prev, [field]: '' }));

        if (field === 'entryLogicDefinitionId' || field === 'entryLogicVersion' || field === 'tradeLogicV2EntryVersionId') {
            setTradeLogicV2EntryCapabilityError(null);
        }

        if (field === 'exitLogicDefinitionId' || field === 'exitLogicVersion' || field === 'tradeLogicV2ExitVersionId') {
            setTradeLogicV2ExitCapabilityError(null);
        }
    };

    const handleEntryAttachmentChange = ({ definitionId, version, versionId }: TradeLogicAttachmentChange) => {
        setFormData((previousFormData) => applyEntryTradeLogicAttachmentChange(previousFormData, { definitionId, version, versionId }));

        setValidationErrors((previousValidationErrors) => ({
            ...previousValidationErrors,
            entryLogicDefinitionId: '',
            entryLogicVersion: '',
            tradeLogicV2EntryVersionId: ''
        }));

        setTradeLogicV2EntryCapabilityError(null);
    };

    const handleExitAttachmentChange = ({ definitionId, version, versionId }: TradeLogicAttachmentChange) => {
        setFormData((previousFormData) => applyExitTradeLogicAttachmentChange(previousFormData, { definitionId, version, versionId }));

        setValidationErrors((previousValidationErrors) => ({
            ...previousValidationErrors,
            exitLogicDefinitionId: '',
            exitLogicVersion: '',
            tradeLogicV2ExitVersionId: ''
        }));

        setTradeLogicV2ExitCapabilityError(null);
    };

    useEffect(() => {
        const { attachment, cleanedSearchParams, hadAttachParams } = consumeTradeLogicAttachmentSearchParams(searchParams);

        if (!hadAttachParams) return;

        pendingRouteTradeLogicAttachmentRef.current = attachment;

        const replaceSearch = () =>
            navigate(
                {
                    pathname: location.pathname,
                    search: cleanedSearchParams.toString() ? `?${cleanedSearchParams.toString()}` : ''
                },
                { replace: true }
            );

        if (attachment === null) {
            void replaceSearch();
            return;
        }

        queueMicrotask(() => {
            if (attachment.kind === 'entry') {
                handleEntryAttachmentChange(attachment);
            } else {
                handleExitAttachmentChange(attachment);
            }

            void replaceSearch();
        });
    }, [location.pathname, navigate, searchParams]);

    function handleTradeLogicCapabilityMutationError(mutationError: object): boolean {
        const capabilityError = getTradeLogicCapabilityError(mutationError);
        if (!capabilityError) return false;

        // The backend names the incompatible rule set, so match it against the staged entry definition;
        // anything else is an exit-side problem.
        const stagedEntryDefinitionName = selectedTradeLogicV2EntryVersionQuery.data?.definition?.name ?? null;
        const isEntryCapabilityError = stagedEntryDefinitionName !== null && stagedEntryDefinitionName === capabilityError.definitionName;

        if (isEntryCapabilityError) {
            setTradeLogicV2EntryCapabilityError(capabilityError);
        } else {
            setTradeLogicV2ExitCapabilityError(capabilityError);
        }

        // Reveal the section holding the error card rather than failing silently on a hidden pane.
        if (isEditMode) {
            selectSection(isEntryCapabilityError ? 'entry' : 'exit');
        }

        return true;
    }

    const validateForm = (): boolean => {
        const errors = buildTradingStrategyValidationErrors(formData);

        setValidationErrors(errors);

        const sectionWithError = resolveStrategyEditorSectionForValidationErrors(errors);

        if (sectionWithError !== null && isEditMode) {
            selectSection(sectionWithError);
        }

        return Object.keys(errors).length === 0;
    };

    const commitStrategyChanges = async () => {
        setSaveError(null);
        setTradeLogicV2EntryCapabilityError(null);
        setTradeLogicV2ExitCapabilityError(null);
        setIsSubmitWorkflowPending(true);

        try {
            let currentStrategyId = strategyId;

            if (!hasRequiredTradeLogicV2Attachments(formData)) {
                setSaveError('Select exact saved entry and exit rule versions before saving the strategy.');
                return;
            }

            if (isEditMode && strategyId) {
                const updateResult = await updateStrategyMutation.mutateAsync(
                    buildUpdateTradingStrategyInput(strategyId, formData, {
                        includeRegimeModelVersionOverrides: hasStagedRegimePinChange
                    })
                );
                if (!updateResult.success) return;

                // The stale-lineage banner judges reports against the strategy's pins and the current
                // actives, so a pin change must refresh both feeds immediately — everywhere, not just
                // this editor's own strategy query.
                if (hasStagedRegimePinChange) {
                    await Promise.all([
                        queryClient.invalidateQueries(trpc.regimeProfile.listForStrategy.pathFilter()),
                        queryClient.invalidateQueries(trpcUnbatched.strategy.fetchStrategies.pathFilter())
                    ]);
                }
            } else {
                const createResult = await createStrategyMutation.mutateAsync(buildCreateTradingStrategyInput(formData));
                if (!createResult.success) return;
                currentStrategyId = createResult?.id ?? null;
            }

            if (currentStrategyId) {
                await executeTradingStrategyLegMutations(buildTradingStrategyLegMutationInputs(formData, currentStrategyId), {
                    createLeg: createLegMutation.mutateAsync,
                    deleteLeg: deleteLegMutation.mutateAsync,
                    updateLeg: updateLegMutation.mutateAsync
                });
            }

            if (isEditMode && strategyId !== null) {
                if (screenerChanges.length > 0) {
                    const screenerResult = await attachScreenerToStrategyMutation.mutateAsync({
                        strategyId,
                        screenerRuleId: strategyScreenerRuleId
                    });

                    if (!screenerResult.success) {
                        showToast(screenerResult.errorMessages[0] ?? 'Failed to update screener', 'error');
                        return;
                    }

                    const optimizerNotice = buildOptimizerConfirmationNotice(screenerResult.optimizerConfirmationAdvisory);

                    if (optimizerNotice !== null) {
                        showToast(optimizerNotice, 'warning');
                    }
                }

                try {
                    await globalExitCandidatesCardRef.current?.savePendingChanges();
                } catch (candidateError) {
                    const candidateErrorMessages = parseTRPCError(candidateError, 'Failed to update strategy exit candidates');
                    showToast(candidateErrorMessages[0] ?? 'Failed to update strategy exit candidates', 'error');
                    return;
                }

                if (hasStagedAttachmentChange) {
                    await invalidatePublishedTradeLogicQueries(queryClient);
                }

                // Refetch before clearing the hydration key: the key reset is what re-runs hydration, and it
                // has to read the refreshed strategy so the new baseline matches what was just persisted.
                await strategyQuery.refetch();
                setFormHydrationKey(null);

                showToast('Strategy updated successfully', 'success');
            }

            if (!isEditMode) {
                void navigate(currentStrategyId === null ? '/user/tradingStrategies' : `/user/tradingStrategy/${currentStrategyId}`);
            }
        } catch (error) {
            if (typeof error === 'object' && error !== null && handleTradeLogicCapabilityMutationError(error)) {
                return;
            }

            const fallbackErrorMessage = isEditMode ? 'Failed to update strategy' : 'Failed to create strategy';
            const errorMessages = parseTRPCError(error, fallbackErrorMessage);

            // The mutations already raise their own error toasts; this only adds the persistent
            // in-place explanation next to the changes that failed to save.
            setSaveError(errorMessages[0] ?? fallbackErrorMessage);
        } finally {
            setIsSubmitWorkflowPending(false);
        }
    };

    /**
     * Guarded save flow. Regime pin changes and live attachment changes each require their own explicit
     * confirmation before anything is persisted; cancelling either dialog fires no mutation. The pin
     * guard exists because re-pointing classifier versions wedges comparability with every experiment
     * and profile built under the old resolution.
     */
    const runStrategySaveGate = (gate: StrategySaveGate) => {
        if (gate === 'regimePinConfirmation') {
            setShowRegimePinSaveConfirm(true);
            return;
        }

        if (gate === 'attachmentConfirmation') {
            setShowAttachmentSaveConfirm(true);
            return;
        }

        void commitStrategyChanges();
    };

    const handleSaveRequested = () => {
        if (!validateForm()) return;

        runStrategySaveGate(
            resolveStrategySaveGate({
                attachmentChangeConfirmed: false,
                hasStagedAttachmentChange: Boolean(isEditMode) && hasStagedAttachmentChange,
                hasStagedRegimePinChange: Boolean(isEditMode) && hasStagedRegimePinChange,
                regimePinChangeConfirmed: false
            })
        );
    };

    const handleDiscardChanges = () => {
        if (baselineFormData !== null) {
            // The baseline carries version ids but not the resolved definition metadata, which is
            // backfilled asynchronously. Restoring the live definition alongside it keeps the rule
            // cards from flashing "no rules attached" before the backfill catches up.
            setFormData({
                ...baselineFormData,
                entryLogicDefinitionId: liveEntryAttachmentDefinitionId,
                entryLogicVersion: liveEntryAttachmentVersion,
                exitLogicDefinitionId: liveExitAttachmentDefinitionId,
                exitLogicVersion: liveExitAttachmentVersion
            });
        }

        setStrategyScreenerRuleId(persistedScreenerRuleId);
        globalExitCandidatesCardRef.current?.discardPendingChanges();
        setValidationErrors({});
        setSaveError(null);
        setTradeLogicV2EntryCapabilityError(null);
        setTradeLogicV2ExitCapabilityError(null);
    };

    const handleDelete = () => {
        if (strategyId) {
            setShowDeleteConfirm(true);
        }
    };

    const handleConfirmDelete = async () => {
        if (strategyId) {
            await deleteStrategyMutation.mutateAsync({ id: strategyId });
        }
    };

    const persistedTradeLogicV2EntryAttachment =
        !hasPersistedTradeLogicV2Entry || liveEntryAttachmentDefinitionId == null
            ? null
            : {
                  definitionId: liveEntryAttachmentDefinitionId,
                  version: liveEntryAttachmentVersion,
                  label: 'Entry journals'
              };

    const persistedTradeLogicV2ExitAttachment =
        !hasPersistedTradeLogicV2Exit || liveExitAttachmentDefinitionId == null
            ? null
            : {
                  definitionId: liveExitAttachmentDefinitionId,
                  version: liveExitAttachmentVersion,
                  label: 'Exit journals'
              };

    const isSavingStrategy =
        isSubmitWorkflowPending ||
        createStrategyMutation.isPending ||
        updateStrategyMutation.isPending ||
        attachScreenerToStrategyMutation.isPending ||
        createLegMutation.isPending ||
        updateLegMutation.isPending ||
        deleteLegMutation.isPending;

    const canEditFields = canEditStrategy && isOwnedStrategy;
    const visibleLegCount = countVisibleTradingStrategyLegs(formData.legs);

    const firstVisibleLeg = formData.legs.find((leg) => leg.isDeleted !== true) ?? null;

    const legSummary =
        visibleLegCount > 1 ? `${visibleLegCount} legs` : firstVisibleLeg === null ? '—' : getTradingStrategyLegSummary(firstVisibleLeg);

    const metaBySection: Partial<Record<StrategyEditorSectionId, string>> = {
        entry: liveEntryAttachmentVersion === null ? undefined : `v${liveEntryAttachmentVersion}`,
        exit: liveExitAttachmentVersion === null ? undefined : `v${liveExitAttachmentVersion}`,
        position: String(visibleLegCount)
    };

    const overviewAttentionCount =
        (liveEntryAttachmentVersion === null ? 1 : 0) +
        (liveExitAttachmentVersion === null ? 1 : 0) +
        ((backtestCoverage?.missingEntitiesCount ?? 0) > 0 ? 1 : 0) +
        (!formData.active && canEditStrategy ? 1 : 0) +
        (overviewLiveReadiness.state === 'failed' || (overviewLiveReadiness.state === 'checked' && overviewLiveReadiness.gap !== null)
            ? 1
            : 0);

    // Render loading state
    if (loadingState.isLoading) {
        return (
            <UserLayout title={`${isEditMode ? 'Edit' : 'Create'} Trading Strategy - Project 314`}>
                <DataLoadingSpinner text={loadingState.loadingText} />
            </UserLayout>
        );
    }

    if (loadingState.showMissingStrategy) {
        return (
            <UserLayout title="Edit Trading Strategy - Not Found - Project 314">
                <div className="container mx-auto">
                    <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md">
                        Trading strategy not found or you do not have access to view it.
                    </div>
                </div>
            </UserLayout>
        );
    }

    // Render error state
    if (error) {
        return (
            <UserLayout title={`${isEditMode ? 'Edit' : 'Create'} Trading Strategy - Error - Project 314`}>
                <div className="container mx-auto">
                    <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md">{error}</div>
                    <Button variant="outline-dark" onClick={() => setError(null)}>
                        Back to the editor
                    </Button>
                </div>
            </UserLayout>
        );
    }

    const renderActiveSection = () => {
        switch (activeSectionId) {
            case 'overview':
                return strategyId === null ? null : (
                    <StrategyOverviewPane
                        attachedCandidateCount={attachedCandidateCount}
                        canEditStrategy={canEditStrategy}
                        coverage={
                            backtestCoverage === null
                                ? null
                                : {
                                      entitiesWithBacktestsCount: backtestCoverage.entitiesWithBacktestsCount,
                                      missingEntitiesCount: backtestCoverage.missingEntitiesCount,
                                      percent: backtestCoveragePercent,
                                      totalEntitiesInUniverse: backtestCoverage.totalEntitiesInUniverse
                                  }
                        }
                        defaultCandidateId={defaultCandidateId}
                        duration={formData.duration}
                        entityTypeLabels={formData.allowedUnderlyingEntityTypes}
                        entryAttachment={{
                            name: liveEntryAttachmentDefinition?.name ?? null,
                            ownerLabel:
                                liveEntryAttachmentDefinition === null
                                    ? null
                                    : liveEntryAttachmentDefinition.ownerType === 'strategy'
                                      ? 'Strategy-owned'
                                      : 'Shared library',
                            version: liveEntryAttachmentVersion
                        }}
                        exitAttachment={{
                            name: liveExitAttachmentDefinition?.name ?? null,
                            ownerLabel:
                                liveExitAttachmentDefinition === null
                                    ? null
                                    : liveExitAttachmentDefinition.ownerType === 'strategy'
                                      ? 'Strategy-owned'
                                      : 'Shared library',
                            version: liveExitAttachmentVersion
                        }}
                        hasEntityListScope={formData.entityListId !== null}
                        isActive={formData.active}
                        isOwnedStrategy={isOwnedStrategy}
                        legCount={visibleLegCount}
                        legSummary={legSummary}
                        liveReadiness={overviewLiveReadiness}
                        screenerRuleId={strategyScreenerRuleId}
                        strategyId={strategyId}
                        onActivate={() => handleInputChange('active', true)}
                        onNavigate={selectSection}
                        onRecheckLiveReadiness={() => void liveReadinessQuery.refetch()}
                    />
                );

            case 'setup':
                return (
                    <fieldset className="flex min-w-0 flex-col gap-5" disabled={!canEditStrategy}>
                        <div>
                            <h4 className="text-base font-semibold text-white">Setup</h4>
                            <p className="mt-1 max-w-[58ch] text-xs text-zinc-500">
                                Identity, universe and session timing. Toggle active state from the header.
                            </p>
                        </div>
                        <BasicInformationCard
                            formData={formData}
                            nameInputRef={strategyNameInputRef}
                            validationErrors={validationErrors}
                            onInputChange={handleInputChange}
                        />
                        <TradingConfigurationCard
                            formData={formData}
                            validationErrors={validationErrors}
                            onInputChange={handleInputChange}
                        />
                        <SessionTimingPolicyCard
                            sessionTimingPolicy={formData.sessionTimingPolicy}
                            setFormData={setFormData}
                            validationErrors={validationErrors}
                        />
                        <RegimeModelPinsCard
                            isCatalogLoading={tradingRequisitesResult.isLoading}
                            pins={formData.regimeModelVersionOverrides}
                            regimeModelVersions={tradingRequisitesResult.requisites?.regimeModelVersions ?? null}
                            onChange={(nextPins) => handleInputChange('regimeModelVersionOverrides', nextPins)}
                        />
                    </fieldset>
                );

            case 'entry':
                return (
                    <div className="flex flex-col gap-5">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <h4 className="text-base font-semibold text-white">Entry</h4>
                                <p className="mt-1 max-w-[58ch] text-xs text-zinc-500">
                                    Everything that decides whether a position opens: the screener that narrows the universe, the rules that
                                    fire, and the scorecard that ranks what fired.
                                </p>
                            </div>
                            {isEditMode && strategyId !== null && isOwnedStrategy && (
                                <Button size="sm" to={`/user/entryOptimizer/new?strategyId=${strategyId}`} variant="outline-dark">
                                    Entry Optimizer
                                </Button>
                            )}
                        </div>

                        <TradeLogicAttachmentCard
                            canEdit={canEditFields}
                            definitionId={formData.entryLogicDefinitionId}
                            isPersistedStrategy={isEditMode && strategyId !== null}
                            kind="entry"
                            liveVersion={liveEntryAttachmentVersion}
                            liveVersionId={persistedStrategy?.tradeLogicV2EntryVersionId ?? null}
                            version={formData.entryLogicVersion}
                            versionId={formData.tradeLogicV2EntryVersionId}
                            onChange={handleEntryAttachmentChange}
                        />
                        {tradeLogicV2EntryCapabilityError && (
                            <TradeLogicCapabilityErrorCard
                                capabilityError={tradeLogicV2EntryCapabilityError}
                                title="Entry rules compatibility issue"
                            />
                        )}

                        <ScreenerGatingCard
                            canAttachScreener={canAttachScreenerToPersistedStrategy}
                            canEdit={canEditFields}
                            isScreenerAttachmentLoading={isScreenerAttachmentLoading}
                            screenerRuleId={strategyScreenerRuleId}
                            strategyId={strategyId}
                            onScreenerRuleIdChange={(nextScreenerRuleId) => {
                                setIsScreenerAttachmentInitialized(true);
                                setStrategyScreenerRuleId(nextScreenerRuleId);
                            }}
                        />

                        <EntryScorecardCard
                            enabled={canLoadPersistedStrategyAuxiliaryData}
                            isEditMode={isEditMode}
                            isOwnedStrategy={isOwnedStrategy}
                            strategyId={strategyId}
                        />
                    </div>
                );

            case 'exit':
                return (
                    <div className="flex flex-col gap-5">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <h4 className="text-base font-semibold text-white">Exit</h4>
                                <p className="mt-1 max-w-[58ch] text-xs text-zinc-500">
                                    The rules that close a position, and the candidate set those exits are evaluated against.
                                </p>
                            </div>
                            {isEditMode && strategyId !== null && isOwnedStrategy && (
                                <Button size="sm" to={`/user/aero/new?strategyId=${strategyId}`} variant="outline-dark">
                                    AERO
                                </Button>
                            )}
                        </div>

                        <TradeLogicAttachmentCard
                            canEdit={canEditFields}
                            definitionId={formData.exitLogicDefinitionId}
                            isPersistedStrategy={isEditMode && strategyId !== null}
                            kind="exit"
                            liveVersion={liveExitAttachmentVersion}
                            liveVersionId={persistedStrategy?.tradeLogicV2ExitVersionId ?? null}
                            version={formData.exitLogicVersion}
                            versionId={formData.tradeLogicV2ExitVersionId}
                            onChange={handleExitAttachmentChange}
                        />
                        {tradeLogicV2ExitCapabilityError && (
                            <TradeLogicCapabilityErrorCard
                                capabilityError={tradeLogicV2ExitCapabilityError}
                                title="Exit rules compatibility issue"
                            />
                        )}

                        {isEditMode && strategyId !== null && (
                            <GlobalExitCandidatesCard
                                canEdit={canEditFields}
                                ref={globalExitCandidatesCardRef}
                                strategyId={strategyId}
                                onPendingChangesChange={setCandidateChangeLabels}
                            />
                        )}
                    </div>
                );

            case 'position':
                return (
                    <fieldset className="min-w-0" disabled={!canEditStrategy}>
                        {strategyId !== null &&
                            formData.allowedUnderlyingEntityTypes.length === 1 &&
                            formData.allowedUnderlyingEntityTypes[0] === 'Asset' && (
                                <FuturesSignalRoutesCard strategyId={strategyId} canEdit={canEditStrategy} />
                            )}
                        <StrategyLegsSection
                            legs={formData.legs}
                            setFormData={setFormData}
                            setValidationErrors={setValidationErrors}
                            validationErrors={validationErrors}
                        />
                    </fieldset>
                );

            case 'backtests':
                return (
                    <fieldset className="min-w-0" disabled={!canEditStrategy}>
                        <StrategyBacktestsPane
                            formData={formData}
                            isOwnedStrategy={isOwnedStrategy}
                            strategyHasOptionLeg={strategyHasOptionLeg}
                            strategyId={strategyId}
                            validationErrors={validationErrors}
                            onInputChange={handleInputChange}
                        />
                    </fieldset>
                );

            case 'activity':
                return (
                    <div className="flex flex-col gap-5">
                        <div>
                            <h4 className="text-base font-semibold text-white">Activity</h4>
                            <p className="mt-1 max-w-[58ch] text-xs text-zinc-500">
                                What the live systems actually did with the saved attachments. Unsaved edits are not reflected here.
                            </p>
                        </div>
                        <TradeLogicV2ExecutionJournalViewer
                            entryAttachment={persistedTradeLogicV2EntryAttachment}
                            entryJournals={tradeLogicV2EntryJournalsQuery.data?.journals ?? []}
                            exitAttachment={persistedTradeLogicV2ExitAttachment}
                            exitJournals={tradeLogicV2ExitJournalsQuery.data?.journals ?? []}
                            hasEntryError={tradeLogicV2EntryJournalsQuery.error !== null}
                            hasExitError={tradeLogicV2ExitJournalsQuery.error !== null}
                            isEntryLoading={tradeLogicV2EntryJournalsQuery.isLoading}
                            isExitLoading={tradeLogicV2ExitJournalsQuery.isLoading}
                        />
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <UserLayout containerSize="1200px" title={`${isEditMode ? 'Edit' : 'Create'} Trading Strategy - Project 314`}>
            <div className="container mx-auto px-4">
                {isCopyMode && copySourceStrategy && (
                    <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-3 mt-4 mb-4">
                        <p className="text-sm text-blue-300">
                            This is a copy of &ldquo;{copySourceStrategy.name}&rdquo;. Make any desired changes and save to create a new
                            Trading Strategy.
                        </p>
                    </div>
                )}
                <StrategyIdentityHeader
                    attachedCandidateCount={attachedCandidateCount}
                    canEditStrategy={canEditStrategy}
                    coveragePercent={backtestCoveragePercent}
                    formData={formData}
                    isDeletePending={deleteStrategyMutation.isPending}
                    isEditMode={isEditMode}
                    isOwnedStrategy={isOwnedStrategy}
                    liveEntryAttachmentVersion={liveEntryAttachmentVersion}
                    liveExitAttachmentVersion={liveExitAttachmentVersion}
                    strategyId={strategyId}
                    onDelete={handleDelete}
                    onShare={() => setShowShareModal(true)}
                    onToggleActive={() => handleInputChange('active', !formData.active)}
                />

                {isEditMode && (
                    <ResourceAccessNotice access={{ canEdit: canEditStrategy, ownedByUser: isOwnedStrategy }} noun="trading strategy" />
                )}

                {isEditMode ? (
                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[190px_minmax(0,1fr)]">
                        <StrategySectionNav
                            activeSectionId={activeSectionId}
                            attentionCount={overviewAttentionCount}
                            changeCountsBySection={changeCountsBySection}
                            metaBySection={metaBySection}
                            sections={visibleSections}
                            onSelect={selectSection}
                        />
                        {/* Only the active section is mounted, so inactive panes never issue their queries.
                            The h5 override restores readable card titles: the global h5 rule paints them
                            #7c8083, which is too faint for a section heading. */}
                        <div className="min-w-0 [&_h5]:text-white">{renderActiveSection()}</div>
                    </div>
                ) : (
                    <StrategyCreatePane
                        formData={formData}
                        nameInputRef={strategyNameInputRef}
                        setFormData={setFormData}
                        setValidationErrors={setValidationErrors}
                        strategyHasOptionLeg={strategyHasOptionLeg}
                        validationErrors={validationErrors}
                        onEntryAttachmentChange={handleEntryAttachmentChange}
                        onExitAttachmentChange={handleExitAttachmentChange}
                        onInputChange={handleInputChange}
                    />
                )}

                <StrategySaveBar
                    canEdit={canEditStrategy}
                    changes={unsavedChanges}
                    errorMessage={saveError}
                    isSaving={isSavingStrategy}
                    mode={isEditMode ? 'edit' : 'create'}
                    onDiscard={handleDiscardChanges}
                    onSave={handleSaveRequested}
                />
            </div>

            {isEditMode && strategyId && showShareModal && (
                <ShareResourceModal
                    isOpen={showShareModal}
                    resourceLabel={formData.name || `Trading Strategy #${strategyId}`}
                    resourcePk={strategyId}
                    resourceType="tradingStrategy"
                    onClose={() => setShowShareModal(false)}
                />
            )}

            <ConfirmationDialog
                cancelText="Keep current pins"
                confirmText="I understand — change pins"
                confirmVariant="warning"
                isOpen={showRegimePinSaveConfirm}
                message={
                    <div className="space-y-3 text-sm">
                        <p>Saving changes which regime classifier versions this strategy resolves:</p>
                        <ul className="list-disc space-y-1 pl-5">
                            {stagedRegimePinChanges.map((pinChange) => (
                                <li key={`${pinChange.scope}-${pinChange.horizon}`}>{pinChange.label}</li>
                            ))}
                        </ul>
                        <p>
                            Pins take precedence over the active model wherever this strategy resolves regime classifications (parameter
                            discovery and live entries), and the resolved versions are bound into corpus fingerprints — new work will not be
                            comparable with work produced under the old resolution.
                        </p>
                        <p>
                            Experiments and regime profiles that already exist were built under the old resolution: their reports will show
                            the &ldquo;profiled under a superseded model&rdquo; banner, and two profiles of this strategy can legitimately
                            disagree until they are re-run. Any experiment workspace still in flight under the old resolution is wedged
                            fail-closed.
                        </p>
                    </div>
                }
                title="Change regime model pins"
                onCancel={() => setShowRegimePinSaveConfirm(false)}
                onConfirm={() => {
                    setShowRegimePinSaveConfirm(false);
                    runStrategySaveGate(
                        resolveStrategySaveGate({
                            attachmentChangeConfirmed: false,
                            hasStagedAttachmentChange: Boolean(isEditMode) && hasStagedAttachmentChange,
                            hasStagedRegimePinChange: Boolean(isEditMode) && hasStagedRegimePinChange,
                            regimePinChangeConfirmed: true
                        })
                    );
                }}
            />

            <ConfirmationDialog
                cancelText="Keep editing"
                confirmText="Save and go live"
                isOpen={showAttachmentSaveConfirm}
                message={`Saving attaches ${[
                    hasStagedEntryAttachmentChange ? 'a new entry rule version' : null,
                    hasStagedExitAttachmentChange ? 'a new exit rule version' : null
                ]
                    .filter(Boolean)
                    .join(' and ')} to this strategy. Live systems start using it immediately.`}
                title="Change live trading rules"
                onCancel={() => setShowAttachmentSaveConfirm(false)}
                onConfirm={async () => {
                    setShowAttachmentSaveConfirm(false);
                    await commitStrategyChanges();
                }}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                cancelText="Cancel"
                confirmText="Delete"
                confirmVariant="danger"
                isOpen={showDeleteConfirm}
                message="Are you sure you want to delete this strategy? This action cannot be undone."
                title="Delete Trading Strategy"
                onCancel={() => setShowDeleteConfirm(false)}
                onConfirm={handleConfirmDelete}
            />
        </UserLayout>
    );
};

export default TradingStrategyEditor;
